import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import BiometrieInvitation from '../components/common/BiometrieInvitation';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  AttachMoney,
  EventNote,
  LocalAtm,
  VolunteerActivism,
  Article,
  Description,
  Receipt,
  Payments,
  Assessment,
  Settings,
  ReportProblem as DefaillantIcon,
  ExitToApp,
  Notifications,
  Person,
  ExpandLess,
  ExpandMore,
  AccountBalance,
  WhatsApp as WhatsAppIcon,
  Cake as CakeIcon,
  AccountTree as AccountTreeIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { HowToReg as HowToRegIcon, Storefront as StorefrontIcon } from '@mui/icons-material';

const drawerWidth = 280;
const OR = '#D4AF37';

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState({});

  // ── Hooks contexte (doivent être déclarés AVANT d'être utilisés) ─────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // ── Notifications dynamiques ─────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [nbUrgents, setNbUrgents]         = useState(0);
  const [nbTotal, setNbTotal]             = useState(0);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const intervalRef = useRef(null);

  // Tous les rôles bureau voient les notifs live (sauf membre simple)
  const isAdmin = ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire'].includes(user?.role);

  const chargerNotifications = useCallback(async () => {
    if (!user) return;
    setLoadingNotifs(true);
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const endpoint = isAdmin
        ? `${API}/notifications/admin/live`
        : `${API}/notifications/non-lues/count`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        if (isAdmin) {
          setNotifications(data.data.events || []);
          setNbUrgents(data.data.nb_urgents || 0);
          setNbTotal(data.data.nb_total     || 0);
        } else {
          setNbTotal(data.data.count        || 0);
        }
      }
    } catch (_) {
      // silencieux — pas de crash si l'API est offline
    } finally {
      setLoadingNotifs(false);
    }
  }, [user, isAdmin]);

  // Chargement initial + polling toutes les 60 secondes
  useEffect(() => {
    chargerNotifications();
    intervalRef.current = setInterval(chargerNotifications, 60_000);
    return () => clearInterval(intervalRef.current);
  }, [chargerNotifications]);

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
    chargerNotifications(); // Rafraîchir à l'ouverture
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Sur téléphone, le menu doit se refermer dès qu'on a touché une page
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSubMenuClick = (key) => {
    setOpenSubMenu(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Menu items avec sous-menus
  const menuItems = [
    {
      title: 'Tableau de bord',
      path: '/admin/dashboard',
      icon: <Dashboard />,
    },
    {
      title: 'Validation Comptes',
      path: '/admin/validation-comptes',
      icon: <HowToRegIcon />,
      // badge: membresEnAttente?.length
    },
    {
      title: 'Validation Annonces',
      path: '/admin/validation-annonces',
      icon: <StorefrontIcon />,
    },
    {
      title: 'Membres',
      path: '/admin/membres',
      icon: <People />,
    },
    {
      title: 'Finances',
      icon: <AccountBalance />,
      subItems: [
        {
          title: 'Cotisations',
          path: '/admin/cotisations',
          icon: <AttachMoney />,
        },
        {
          title: 'Tontine',
          path: '/admin/tontine',
          icon: <LocalAtm />,
        },
        {
          title: 'Aides',
          path: '/admin/aides',
          icon: <VolunteerActivism />,
        },
        {
          title: 'Dépenses',
          path: '/admin/depenses',
          icon: <Receipt />,
        },
        {
          title: 'Recettes',
          path: '/admin/recettes',
          icon: <Payments />,
          roles: ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'commissaire'],
        },
        {
          title: 'Emprunts',
          path: '/admin/emprunts',
          icon: <AccountBalance sx={{ color: OR }} />,
          roles: ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'commissaire'],
        },
        {
          title: 'Événements',
          path: '/admin/evenements',
          icon: <span style={{ fontSize: 16 }}>🎉</span>,
          roles: ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint'],
        },
        {
          title: 'Comptes Bancaires',
          path: '/admin/comptes-bancaires',
          icon: <AccountBalance sx={{ color: '#D4AF37' }} />,
          roles: ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'commissaire'],
        }
      ]
    },
    {
      title: 'Assemblées',
      path: '/admin/assemblees',
      icon: <EventNote />,
    },
    {
      title: 'Rappels WhatsApp',
      path: '/admin/rappels',
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
      roles: ['administrateur', 'president', 'secretaire_general', 'secretaire_general_adjoint'],
    },
    {
      title: 'Communications',
      icon: <Article />,
      subItems: [
        {
          title: 'Blog',
          path: '/admin/blog',
          icon: <Article />,
        },
        {
          title: 'Comptes Rendus',
          path: '/admin/comptes-rendus',
          icon: <Description />,
        },
        {
          title: 'Messagerie WhatsApp',
          path: '/admin/messagerie',
          icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
          roles: ['administrateur', 'president', 'secretaire_general', 'secretaire_general_adjoint'],
        },
        {
          title: 'Anniversaires',
          path: '/admin/anniversaires',
          icon: <CakeIcon sx={{ color: '#D4AF37' }} />,
          roles: ['administrateur', 'president', 'secretaire_general', 'secretaire_general_adjoint'],
        },
        {
          title: 'Généalogie',
          path: '/admin/genealogie',
          icon: <AccountTreeIcon sx={{ color: '#D4AF37' }} />,
          roles: ['administrateur', 'president'],
        },
        {
            title: 'Documents Officiels',
            path: '/admin/documents-officiels',
            icon: <FolderIcon sx={{ color: '#D4AF37' }} />,
            label: 'Documents Officiels',
        }

      ]
    },
    {
      title: 'Comptabilité',
      path: '/admin/rapports',
      icon: <Assessment />,
      roles: ['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'commissaire'],
    },
    {
      title: 'Défaillants',
      path: '/admin/defaillants',
      icon: <DefaillantIcon />,
    },
    {
      title: "Bilan de l'association",
      path: '/admin/bilan-association',
      icon: <Assessment />,
    },
    {
      title: 'Blog (site public)',
      path: '/blog',
      icon: <Article />,
    },
    {
      title: 'Paramètres',
      path: '/admin/settings',
      icon: <Settings />,
    }
  ];

  // Filtrer les menus selon le rôle
  const getFilteredMenuItems = () => {
    const userRole = user?.role;

    return menuItems.map(item => {
      const filteredItem = { ...item };

      // ── Règle générique : si l'item déclare une liste de rôles autorisés,
      //    masquer pour tout rôle absent de cette liste
      if (item.roles && !item.roles.includes(userRole)) {
        return null;
      }

      // ── Règle générique sous-menus : appliquer les rôles déclarés sur chaque
      //    sous-item (auparavant jamais appliqués → menus visibles à tort)
      if (filteredItem.subItems) {
        filteredItem.subItems = filteredItem.subItems.filter(
          subItem => !subItem.roles || subItem.roles.includes(userRole)
        );
      }

      // ── Paramètres : administrateur et président uniquement
      if (item.title === 'Paramètres') {
        return ['administrateur', 'president'].includes(userRole) ? filteredItem : null;
      }

      // ── Validation Comptes : administrateur et président uniquement
      if (item.title === 'Validation Comptes') {
        return ['administrateur', 'president'].includes(userRole) ? filteredItem : null;
      }

      // ── Validation Annonces : administrateur et président uniquement
      if (item.title === 'Validation Annonces') {
        return ['administrateur', 'president'].includes(userRole) ? filteredItem : null;
      }

      // ── Trésorier et Commissaire : masquer Assemblées, Communications
      //    Membres reste visible en CONSULTATION pour le commissaire (il en a
      //    besoin pour la liste déroulante de saisie des cotisations)
      if (['tresorier', 'tresorier_adjoint', 'commissaire'].includes(userRole)) {
        if (['Assemblées', 'Communications'].includes(item.title)) {
          return null;
        }
        if (item.title === 'Membres' && ['tresorier', 'tresorier_adjoint'].includes(userRole)) {
          return null;
        }
      }

      // ── Secrétaire Général (+ adjoint) : accès partiel — Finances réduit à Événements,
      //    Communications réduit à ses attributions, pas de Rapports/Défaillants
      //    (les API correspondantes lui sont fermées)
      if (['secretaire_general', 'secretaire_general_adjoint'].includes(userRole)) {
        if (['Rapports', 'Défaillants'].includes(item.title)) return null;

        if (item.title === 'Finances' && filteredItem.subItems) {
          filteredItem.subItems = filteredItem.subItems.filter(
            subItem => subItem.title === 'Événements'
          );
          return filteredItem.subItems.length > 0 ? filteredItem : null;
        }

        if (item.title === 'Communications' && filteredItem.subItems) {
          filteredItem.subItems = filteredItem.subItems.filter(
            subItem => ['Comptes Rendus', 'Messagerie WhatsApp', 'Anniversaires'].includes(subItem.title)
          );
          return filteredItem.subItems.length > 0 ? filteredItem : null;
        }
      }

      // ── Masquer un menu parent dont tous les sous-items ont été filtrés
      if (item.subItems && filteredItem.subItems.length === 0) {
        return null;
      }

      return filteredItem;
    }).filter(Boolean);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo et titre */}
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.dark', color: 'white' }}>
        <Typography variant="h5" fontWeight="bold">
          BON'EPEY'A NTOCKO
        </Typography>
        <Chip 
          label={user?.role?.toUpperCase()} 
          size="small" 
          sx={{ mt: 1, bgcolor: 'white', color: 'primary.dark', fontWeight: 'bold' }}
        />
      </Box>
      
      <Divider />

      {/* User info */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            src={user?.photoUrl}
            alt={user?.prenom}
            sx={{ bgcolor: 'primary.main' }}
          >
            {user?.prenom?.[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.prenom} {user?.nom}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.matricule}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {getFilteredMenuItems().map((item) => (
          <React.Fragment key={item.title}>
            {item.subItems ? (
              // Menu avec sous-items
              <>
                <ListItemButton
                  onClick={() => handleSubMenuClick(item.title)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.title} />
                  {openSubMenu[item.title] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openSubMenu[item.title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.path}
                        component={RouterLink}
                        to={subItem.path}
                        selected={location.pathname === subItem.path}
                        sx={{
                          pl: 4,
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white'
                            }
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.title} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Menu simple
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white'
                    },
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider />

      {/* Logout Button */}
      <List sx={{ p: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.lighter'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Déconnexion" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Administration
          </Typography>

          {/* Notifications */}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationMenuOpen}
          >
            <Badge
              badgeContent={nbTotal > 0 ? (nbUrgents > 0 ? nbUrgents : nbTotal) : null}
              color={nbUrgents > 0 ? 'error' : 'primary'}
            >
              <Notifications />
            </Badge>
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar 
              src={user?.photoUrl}
              alt={user?.prenom}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
            >
              {user?.prenom?.[0]}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/admin/profile'); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Mon Profil
        </MenuItem>
        {/* Afficher Paramètres uniquement pour administrateur et président */}
        {['administrateur', 'president'].includes(user?.role) && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/admin/settings'); }}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Paramètres
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        {/* En-tête */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Notifications
            {nbTotal > 0 && (
              <Box component="span" sx={{
                ml: 1, px: 1, py: 0.2, bgcolor: nbUrgents > 0 ? 'error.main' : 'primary.main',
                color: 'white', borderRadius: 10, fontSize: 11, fontWeight: 'bold'
              }}>
                {nbTotal}
              </Box>
            )}
          </Typography>
          {loadingNotifs && <Typography variant="caption" color="text.secondary">Actualisation…</Typography>}
        </Box>
        <Divider />

        {/* Liste des notifications */}
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loadingNotifs ? 'Chargement…' : 'Aucune notification en cours'}
            </Typography>
          </Box>
        ) : (
          notifications.map((notif) => {
            const ICONS = {
              validation_compte:    '👤',
              aide_en_attente:      '🤝',
              cotisation_en_attente:'💰',
              assemblee_proche:     '📅',
              rappel_whatsapp:      '📱',
              nouveau_membre:       '🎉',
              assemblee:            '📅',
              cotisation:           '💰',
              aide:                 '🤝',
              tontine:              '🏆',
              anniversaire:         '🎂',
              general:              '📢',
            };
            const couleurFond = notif.priorite === 'haute' ? '#fff8f8' : 'transparent';
            const couleurBordure = notif.priorite === 'haute' ? '#f44336' : notif.priorite === 'normale' ? '#1976d2' : '#9e9e9e';

            return (
              <MenuItem
                key={notif.id}
                onClick={() => {
                  handleNotificationMenuClose();
                  if (notif.lien) navigate(notif.lien);
                }}
                sx={{
                  py: 1.5, px: 2,
                  bgcolor: couleurFond,
                  borderLeft: `3px solid ${couleurBordure}`,
                  mb: 0.5,
                  whiteSpace: 'normal',
                  alignItems: 'flex-start',
                  '&:hover': { bgcolor: couleurFond === 'transparent' ? 'action.hover' : '#fff0f0' }
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                  <Typography sx={{ fontSize: 20, mt: 0.2 }}>
                    {ICONS[notif.type] || '📢'}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                      {notif.titre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                      {notif.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                      {new Date(notif.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            );
          })
        )}

        <Divider />
        <MenuItem
          onClick={() => { handleNotificationMenuClose(); chargerNotifications(); }}
          sx={{ justifyContent: 'center', py: 1 }}
        >
          <Typography variant="body2" color="primary" fontWeight="bold">
            🔄 Actualiser les notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,            // un contenu trop large ne doit plus elargir la page sur telephone
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: 'grey.50',
          minHeight: '100vh'
        }}
      >
        <Outlet />
        <BiometrieInvitation />
      </Box>
    </Box>
  );
};

export default AdminLayout;
